// src/components/parent/ParentDashboard.tsx
// Updated to include insights view

import React, { useState, useEffect } from 'react'
import { User, Plus, Settings, LogOut, Shield, Eye, EyeOff, BarChart3, Heart, Sparkles, CheckCircle, UserPlus } from 'lucide-react'
import { signOut } from 'firebase/auth'
import { collection, query, where, getDocs } from 'firebase/firestore'
import { auth, db } from '../../lib/firebase-config'
import { useParentAuth } from '../../contexts/ParentAuthContext'
import { Button } from '@/components/ui/Button'
import type { ChildProfile } from '../../types/auth'
import { ChildCreationModal } from './ChildCreationModal'
import ParentInsightsView from './ParentInsightsView'

type TabType = 'children' | 'insights'

export const ParentDashboard: React.FC = () => {
  const { user } = useParentAuth()
  const [children, setChildren] = useState<ChildProfile[]>([])
  const [loading, setLoading] = useState(true)
  const [showChildCreationModal, setShowChildCreationModal] = useState(false)
  const [activeTab, setActiveTab] = useState<TabType>('insights')
  const [selectedChildId, setSelectedChildId] = useState<string>('')

  const fetchChildren = async () => {
    if (!user) return
    
    try {
      const childrenQuery = query(
        collection(db, 'children'),
        where('parentId', '==', user.uid)
      )
      const snapshot = await getDocs(childrenQuery)
      const childrenData = snapshot.docs.map(doc => ({
        ...doc.data(),
        id: doc.id
      } as ChildProfile))

      setChildren(childrenData)
      
      // Auto-select first child and switch to insights
      if (childrenData.length > 0 && !selectedChildId) {
        setSelectedChildId(childrenData[0].childId)
        setActiveTab('insights')
      }
    } catch (error) {
      console.error('Error fetching children:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchChildren()
  }, [user])

  const handleSignOut = async () => {
    try {
      await signOut(auth)
    } catch (error) {
      console.error('Sign out error:', error)
    }
  }

  const handleChildCreated = (childId: string) => {
    setShowChildCreationModal(false)
    fetchChildren()
  }

  if (loading) {
    return (
      <div className="page-container">
        <div className="container">
          <div className="content-area text-center">
            <div className="w-8 h-8 border-4 border-indigo-600/30 border-t-indigo-600 
                          rounded-full animate-spin mx-auto mb-4" />
            <p className="text-body-text">Loading dashboard...</p>
          </div>
        </div>
      </div>
    )
  }

  const selectedChild = children.find(child => child.id === selectedChildId)

  return (
    <div className="page-container">
      <div className="container">
        
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-header-primary mb-1">
                      {selectedChild ? `${selectedChild.username}'s Learning Journey` : 'Family Dashboard'}
            </h1>
            <p className="text-sm text-body-text">
              {children.length} {children.length === 1 ? 'child' : 'children'} • {user?.email}
            </p>
          </div>
                  
          <Button
            onClick={handleSignOut}
            variant="outline"
            className="flex items-center gap-2"
          >
            <LogOut className="w-4 h-4" />
            Sign Out
          </Button>
        </div>

        {/* Quick Actions Bar */}
        {children.length > 0 && (
          <div className="flex items-center justify-between mb-6 pb-4 border-b">
            <div className="flex items-center gap-4">
              {children.length > 1 && (
                <select
                  value={selectedChildId}
                  onChange={(e) => setSelectedChildId(e.target.value)}
                  className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 
                          bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                >
                  {children.map((child) => (
                    <option key={child.id} value={child.id}>
                      {child.username}
                    </option>
                  ))}
                </select>
              )}
              {children.length === 1 && (
                <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Viewing: {selectedChild?.username}
                </div>
              )}
            </div>
            
            <div className="flex gap-2">
              <Button
                onClick={() => setShowChildCreationModal(true)}
                variant="outline"
                size="sm"
                className="flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Add Child
              </Button>
            </div>
          </div>
        )}

        {/* Empty State - Show when no children exist, regardless of tab */}
        {children.length === 0 && (
          <div className="max-w-2xl mx-auto">
            <div className="bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-950/30 dark:to-purple-950/30 rounded-2xl p-8 md:p-10 border border-indigo-100 dark:border-indigo-800">
              
              {/* Welcome Header */}
              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-indigo-100 dark:bg-indigo-900/50 rounded-full mb-4">
                  <Heart className="w-8 h-8 text-indigo-600 dark:text-indigo-400" />
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-3">
                  Welcome to Your Family's Learning Space
                </h2>
                <p className="text-lg text-gray-600 dark:text-gray-300">
                  We're glad you're here. Let's create a space where your child can learn with confidence.
                </p>
              </div>

              {/* What Makes This Different */}
              <div className="bg-white/60 dark:bg-gray-800/60 rounded-xl p-6 mb-6 space-y-4">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-indigo-600" />
                  What makes this different
                </h3>
                
                <div className="space-y-3 text-sm md:text-base text-gray-700 dark:text-gray-300">
                  <div className="flex gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <p><strong>Your child chooses what feels right each day</strong> — no pressure, no rigid schedules</p>
                  </div>
                  <div className="flex gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <p><strong>Activities adapt to how they're feeling</strong> — overwhelmed days look different from energized days</p>
                  </div>
                  <div className="flex gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <p><strong>Their learning space is theirs alone</strong> — safe, judgment-free, and responsive to their needs</p>
                  </div>
                </div>
              </div>

              {/* What Happens Next */}
              <div className="bg-white/60 dark:bg-gray-800/60 rounded-xl p-6 mb-6">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">What happens next</h3>
                <p className="text-sm md:text-base text-gray-700 dark:text-gray-300 mb-3">
                  When you create your child's profile, they'll get:
                </p>
                <ul className="space-y-2 text-sm md:text-base text-gray-700 dark:text-gray-300">
                  <li className="flex gap-2"><span className="text-indigo-600">→</span> A unique username they choose</li>
                  <li className="flex gap-2"><span className="text-indigo-600">→</span> A private 4-digit PIN (only they know it)</li>
                  <li className="flex gap-2"><span className="text-indigo-600">→</span> Their own learning dashboard</li>
                </ul>
                <p className="text-sm md:text-base text-gray-700 dark:text-gray-300 mt-3">
                  You'll be able to see their progress here, but their space stays theirs.
                </p>
              </div>

              {/* CTA */}
              <div className="text-center">
                <Button 
                  onClick={() => setShowChildCreationModal(true)}
                  size="lg"
                  className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold px-8 py-3 rounded-xl shadow-lg hover:shadow-xl transition-all"
                >
                  <UserPlus className="w-5 h-5 mr-2" />
                  Create Your Child's Learning Space
                </Button>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">
                  Takes about 2 minutes • Completely private • No credit card needed
                </p>
              </div>

            </div>
          </div>
        )}

        {/* Tab Content - Only show when children exist */}
        {children.length > 0 && activeTab === 'children' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-semibold text-header-primary">
                Your Children ({children.length})
              </h2>
              <Button
                onClick={() => setShowChildCreationModal(true)}
                className="flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Add Child
              </Button>
            </div>

            {/* Show child grid */}
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {children.map((child) => (
                <div key={child.id} className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 bg-indigo-100 dark:bg-indigo-900 rounded-full flex items-center justify-center">
                      <User className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        {child.username}
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Age {child.age} • Added {new Date(child.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Button
                      onClick={() => {
                        setSelectedChildId(child.id)
                        setActiveTab('insights')
                      }}
                      variant="outline"
                      className="w-full flex items-center gap-2"
                    >
                      <BarChart3 className="w-4 h-4" />
                      View Learning Journey
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'insights' && selectedChild && (
          <div>
            {/* Child Selector */}
            {children.length > 1 && (
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  View insights for:
                </label>
                <select
                  value={selectedChildId}
                  onChange={(e) => setSelectedChildId(e.target.value)}
                  className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 
                           bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  {children.map((child) => (
                    <option key={child.id} value={child.id}>
                      {child.username}
                    </option>
                  ))}
                </select>
              </div>
            )}
            
            <ParentInsightsView 
              childId={selectedChildId} 
              childName={selectedChild.username}
            />
          </div>
        )}

        {/* Child Creation Modal */}
        <ChildCreationModal
          isOpen={showChildCreationModal}
          onClose={() => setShowChildCreationModal(false)}
          parentId={user?.uid || ''}
          onSuccess={handleChildCreated}
        />

      </div>
    </div>
  )
}